import {
  Component,
  OnDestroy,
  ElementRef,
  ViewChild,
  AfterViewInit,
} from '@angular/core';

interface FloatingWord {
  x: number;       // anchor point (where the particle dot sits)
  y: number;
  vx: number;
  vy: number;
  label: string;
  opacity: number;
  opacityDir: number;
  labelOffsetX: number;  // pill position relative to anchor
  labelOffsetY: number;
}

@Component({
  selector: 'app-particle-background',
  standalone: false,
  templateUrl: './particle-background.component.html',
  styleUrls: ['./particle-background.component.css'],
})
export class ParticleBackgroundComponent implements AfterViewInit, OnDestroy {
  @ViewChild('p5Container', { static: true }) p5Container!: ElementRef<HTMLDivElement>;

  private sketchInstance: any = null;
  private animFrameId: number | null = null;
  private floatingWords: FloatingWord[] = [];
  private readonly wordLabels = ['Resume', 'Skill Fit', 'Personality Fit', 'Team Fit', 'Neuroscience'];
  private canvasEl!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private resizeObserver!: ResizeObserver;

  ngAfterViewInit(): void {
    this.loadP5().then(() => {
      requestAnimationFrame(() => {
        this.initSketch();
        this.initWordOverlay();
        this.animateWords();
      });
    });
  }

  private loadP5(): Promise<void> {
    return new Promise((resolve) => {
      if ((window as any).p5) { resolve(); return; }
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/p5.js/1.9.0/p5.min.js';
      s.onload = () => resolve();
      document.head.appendChild(s);
    });
  }

  private initSketch(): void {
    const host = this.p5Container.nativeElement;
    const P5 = (window as any).p5;
    const COLOR = '#3c3cec';

    // Use a CORS-friendly version of the brain image via allorigins proxy
    const BRAIN_URL = 'assets/images/konvac-brain.jpg';

    const sketch = (p: any) => {
      let stars: any[] = [];
      let randomStars: any[] = [];
      const SMALL = 80;
      let W = host.offsetWidth || window.innerWidth;
      let H = host.offsetHeight || window.innerHeight;
      let sf: number;   // scaleFactor
      let tx: number;   // translate x
      let ty: number;   // translate y

      const tp = (v: any) => p.createVector(v.x * sf + tx, v.y * sf + ty);

      class Star {
        pos: any; target: any; vel: any; acc: any;
        size: number; weight: number; allowRepulse = false;

        constructor(target: any) {
          this.pos    = p.createVector(p.random(W * -1.2, W * 1.2), p.random(H * -1.2, H * 1.2));
          this.target = target.copy();
          this.vel    = p.createVector(p.random(-5, 6), p.random(-4, 6));
          this.acc    = p.createVector();
          this.size   = p.random(2, 4);
          this.weight = p.random(0.5, 2.5);
        }

        update() { this.pos.add(this.vel); this.vel.add(this.acc); this.acc.mult(0); }

        alpha() {
          return p.map(P5.Vector.dist(this.pos, this.target), 0, 800, 220, 0);
        }

        move(mx: number, my: number) {
          // attract to target
          const toTarget = P5.Vector.sub(this.target, this.pos);
          const d = toTarget.mag();
          if (d < 10) {
            toTarget.setMag((toTarget.magSq() / 70) * this.weight);
            this.vel.limit(0.7);
          } else {
            toTarget.setMag(400000 / toTarget.magSq());
            this.vel.limit(7);
          }
          this.acc.add(toTarget);

          // repulse from mouse
          const toMouse = P5.Vector.sub(p.createVector(mx, my), this.pos);
          if (toMouse.mag() < 80) {
            toMouse.setMag(-(500 / toMouse.magSq()) * this.weight);
            this.acc.add(toMouse);
          }
        }

        render() {
          p.noStroke();
          const c = p.color(COLOR);
          c.setAlpha(this.alpha());
          p.fill(c);
          p.ellipse(this.pos.x, this.pos.y, this.size, this.size);
        }
      }

      class Dot {
        pos: any; vel: any; size: number; alpha = 0;

        constructor() {
          this.pos  = p.createVector(p.random(W), p.random(H));
          this.vel  = p.createVector(p.random(-1.2, 1.2), p.random(-1.2, 1.2));
          this.size = p.random(1.5, 3);
        }

        update() {
          if (this.pos.x > W || this.pos.x < 0) this.vel.x *= -1;
          if (this.pos.y > H || this.pos.y < 0) this.vel.y *= -1;
          this.pos.add(this.vel);
          this.alpha = p.map(P5.Vector.dist(this.pos, p.createVector(0, 0)), 0, W * 0.8, 0, 130);
        }

        render() {
          const c = p.color(COLOR); c.setAlpha(this.alpha);
          p.fill(c); p.noStroke();
          p.ellipse(this.pos.x, this.pos.y, this.size, this.size);
        }
      }

      const recalc = () => {
        sf = (W * 0.35) / SMALL;
        tx = W * 0.12;
        ty = H / 2 - (W * 0.35) / 2;
      };

      const buildFromImage = (img: any) => {
        stars = [];
        img.resize(SMALL, SMALL);
        img.loadPixels();
        for (let x = 0; x < img.width; x++) {
          for (let y = 0; y < img.height; y++) {
            const idx = (x + y * img.width) * 4;
            // dark pixels only (brain outline)
            if (img.pixels[idx] < 160 && Math.random() < 0.65) {
              stars.push(new Star(tp(p.createVector(x, y))));
            }
          }
        }
      };

      p.setup = () => {
        W = host.offsetWidth  || window.innerWidth;
        H = host.offsetHeight || window.innerHeight;
        recalc();

        const cnv = p.createCanvas(W, H);
        // inject canvas BEFORE the word-overlay so it sits behind
        const overlay = host.querySelector('canvas.word-overlay');
        if (overlay) host.insertBefore(cnv.elt, overlay);
        else cnv.parent(host);

        p.pixelDensity(1);

        for (let i = 0; i < 100; i++) randomStars.push(new Dot());

        // Load image — p5 handles crossOrigin internally
        p.loadImage(BRAIN_URL, (img: any) => buildFromImage(img));
      };

      p.draw = () => {
        p.background(255);
        const mx = p.mouseX, my = p.mouseY;
        for (const s of stars) { s.move(mx, my); s.update(); s.render(); }
        for (const d of randomStars) { d.update(); d.render(); }
      };

      p.windowResized = () => {
        W = host.offsetWidth  || window.innerWidth;
        H = host.offsetHeight || window.innerHeight;
        p.resizeCanvas(W, H);
      };
    };

    this.sketchInstance = new P5(sketch);
  }

  private initWordOverlay(): void {
    const host = this.p5Container.nativeElement;
    const overlay = host.querySelector('canvas.word-overlay') as HTMLCanvasElement;
    if (!overlay) return;

    this.canvasEl = overlay;
    this.ctx = overlay.getContext('2d')!;

    const resize = () => {
      const W = host.offsetWidth  || window.innerWidth;
      const H = host.offsetHeight || window.innerHeight;
      this.canvasEl.width  = W;
      this.canvasEl.height = H;
      this.spawnBubbles(W, H);
    };

    resize();

    this.resizeObserver = new ResizeObserver(() => resize());
    this.resizeObserver.observe(host);
  }

  private spawnBubbles(W: number, H: number): void {
    const count = this.wordLabels.length;
    // alternate label offsets so pills don't all sit the same side
    const offsets = [
      { ox: 18, oy: -32 },
      { ox: -14, oy: -36 },
      { ox: 20, oy: -30 },
      { ox: -16, oy: -34 },
    ];
    this.floatingWords = this.wordLabels.map((label, i) => {
      const colW = W / count;
      const off = offsets[i % offsets.length];
      return {
        label,
        x: colW * i + colW * 0.15 + Math.random() * colW * 0.7,
        y: H * 0.15 + Math.random() * H * 0.65,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        opacity: 0.5 + Math.random() * 0.3,
        opacityDir: Math.random() > 0.5 ? 1 : -1,
        labelOffsetX: off.ox,
        labelOffsetY: off.oy,
      };
    });
  }

  private animateWords(): void {
    const FONT = `500 12px 'DM Sans', sans-serif`;
    const PAD_X = 12;
    const PAD_Y = 6;
    const CORNER = 20;
    const DOT_R  = 3;

    const tick = () => {
      if (!this.ctx || !this.canvasEl) { this.animFrameId = requestAnimationFrame(tick); return; }
      const W = this.canvasEl.width;
      const H = this.canvasEl.height;
      this.ctx.clearRect(0, 0, W, H);
      this.ctx.font = FONT;

      for (const b of this.floatingWords) {
        // bounce anchor off edges with a small margin
        const margin = 60;
        if (b.x < margin || b.x > W - margin) b.vx *= -1;
        if (b.y < margin || b.y > H - margin) b.vy *= -1;
        b.x += b.vx;
        b.y += b.vy;
        b.opacity += b.opacityDir * 0.003;
        if (b.opacity > 0.82 || b.opacity < 0.28) b.opacityDir *= -1;

        const alpha = b.opacity;

        // measure text for pill size
        const tw = this.ctx.measureText(b.label).width;
        const pillW = tw + PAD_X * 2;
        const pillH = 12 + PAD_Y * 2;   // line-height ~12px

        // pill top-left corner, offset from anchor
        const px = b.x + b.labelOffsetX - pillW / 2;
        const py = b.y + b.labelOffsetY - pillH / 2;

        // connector line: anchor dot → nearest edge of pill
        const pillCx = px + pillW / 2;
        const pillCy = py + pillH / 2;

        // draw thin connector line
        this.ctx.beginPath();
        this.ctx.moveTo(b.x, b.y);
        this.ctx.lineTo(pillCx, pillCy);
        this.ctx.strokeStyle = `rgba(60,60,236,${alpha * 0.25})`;
        this.ctx.lineWidth = 1;
        this.ctx.stroke();

        // draw anchor dot (small particle-like dot)
        this.ctx.beginPath();
        this.ctx.arc(b.x, b.y, DOT_R, 0, Math.PI * 2);
        this.ctx.fillStyle = `rgba(60,60,236,${alpha * 0.7})`;
        this.ctx.fill();

        // draw pill background
        this.ctx.beginPath();
        this.ctx.roundRect(px, py, pillW, pillH, CORNER);
        this.ctx.fillStyle   = `rgba(60,60,236,${alpha * 0.08})`;
        this.ctx.fill();
        this.ctx.strokeStyle = `rgba(60,60,236,${alpha * 0.45})`;
        this.ctx.lineWidth   = 1;
        this.ctx.stroke();

        // draw label text inside pill
        this.ctx.fillStyle    = `rgba(60,60,236,${alpha})`;
        this.ctx.textAlign    = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillText(b.label, pillCx, pillCy);
      }

      this.animFrameId = requestAnimationFrame(tick);
    };
    this.animFrameId = requestAnimationFrame(tick);
  }

  ngOnDestroy(): void {
    if (this.sketchInstance) this.sketchInstance.remove();
    if (this.animFrameId !== null) cancelAnimationFrame(this.animFrameId);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }
}